import React, { useMemo, useEffect, useState } from 'react'
import moment from 'moment'
import { useSelector, useDispatch } from 'react-redux'
import classNames from 'classnames'

import styles from '../component-styles/share-anomaly-button.module.scss'
import Button from './button'
import Modal, { ModalHead, ModalBody, ModalFooter } from './modal'
import Heading from './heading'
import ConfirmationButtons from './confirmation-buttons'
import { SelectBoxSimple } from './select-box'
import { sharingPermissionTypes } from '../core/variables'
import {
  createSharingLink,
  getSharingLinkData,
  updateSharingLink,
  shareSharingLink,
} from '../api-client/sharing-client'
import Input, { Label, PassiveInput } from './input'
import { FormRow, LabelSlot, FieldSlot } from './row'
import AddMultiValuesTags from './add-multi-values-tags'
import { getUserListAsync } from '../store/actions'
import ToolTip from './tooltip'
import { Preloader } from './loader'

export function getShareEmails(shareEmails, typedValue): string[] {
  if (typedValue === '') {
    return shareEmails
  }
  if (shareEmails.length > 0) {
    if (shareEmails[shareEmails.length - 1] === typedValue) {
      return shareEmails
    }
    return [...shareEmails, ...[typedValue]]
  }
  return [typedValue]
}

export default function ShareAnomalyButton(): React.ReactElement {
  const dispatch = useDispatch()
  const {
    anomaly,
    site: { selected },
  } = useSelector((s: any): any => s)
  const [sharingLinkData, setSharingLinkData] = useState({
    sharingPermission: '',
    publicLink: '',
    shared: false,
  })
  // const [submissionStatus, setSubmissionStatus] = useState<'' | 'success' | 'failed'>('')
  const [active, setActive] = useState(false)
  const [loading, setLoading] = useState(false)
  const [typedValue, setTypedValue] = useState('')
  const [shareEmails, setShareEmails] = useState<string[]>([])
  const [note, setNote] = useState('')
  const [permission, setPermission] = useState<'private' | 'public' | 'off'>(
    'private',
  )
  const { shareLinkID, displayName, anomalyDate } = anomaly?.description || {}

  useEffect(() => {
    dispatch(getUserListAsync())
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (shareLinkID !== null) {
      // eslint-disable-next-line
      ;(async function getSharignDetails() {
        const result = await getSharingLinkData({ shareLinkID })
        setSharingLinkData({ ...sharingLinkData, ...result })
        const { sharingPermission } = result
        setPermission(sharingPermission)
      })()
    }
    // eslint-disable-next-line
  }, [shareLinkID])

  useEffect(() => {
    // if  {
    const { origin, pathname } = window.location
    const originUrl = `${origin}${pathname}`
    if (
      shareLinkID === '' &&
      (permission === 'public' || permission === 'private')
    ) {
      setLoading(true)
      ;(async function getContent() {
        const result = await createSharingLink({
          sharingPermission: permission,
          originUrl,
        })
        setSharingLinkData({ ...sharingLinkData, ...result })
        setLoading(false)
      })()
    } else if (
      sharingLinkData.sharingPermission !== '' &&
      sharingLinkData.sharingPermission !== permission
    ) {
      setLoading(true)
      ;(async function getContent() {
        await updateSharingLink({
          sharingPermission: permission,
          shareLinkID,
          originUrl,
        })
        setSharingLinkData({
          ...sharingLinkData,
          sharingPermission: permission,
        })
        setLoading(false)
      })()
    }
    // }
    // eslint-disable-next-line
  }, [permission])

  const userList = useMemo(() => {
    if (selected && selected.permissionList) {
      const res = [...selected.permissionList, ...selected.pendingUserList].map(
        (item) => item.email,
      )
      return res
    }
    return []
  }, [selected])

  const { sharingPermission, publicLink, shared } = sharingLinkData

  return (
    <div className={styles.container}>
      <div className={styles.buttonWrapper}>
        {sharingPermission && (
          <div className={styles.buttonStatus}>
            <span
              className={classNames({
                [styles.private]: sharingPermission === 'private',
                [styles.public]: sharingPermission !== 'public',
              })}
            >
              public
            </span>
          </div>
        )}
        <Button
          className={styles.button}
          type="share"
          align="right"
          onClick={() => {
            setActive(true)
            setTypedValue('')
            setShareEmails([])
            setSharingLinkData({
              ...sharingLinkData,
              shared: false,
            })
          }}
        >
          Share
        </Button>
      </div>
      {shared && active && (
        <Modal
          type="white"
          narrow
          active={active}
          toggleActive={() => {
            setActive(false)
          }}
        >
          <ModalHead>
            <Heading type={3} align="left" className={styles.heading}>
              Share anomaly
            </Heading>
          </ModalHead>
          <ModalBody>
            <p>Success! Your anomaly has been sent.</p>
          </ModalBody>
          <ModalFooter>
            <ConfirmationButtons
              type="simple"
              className={styles.shareSingleButton}
              onYes={(): void => {
                setActive(false)
              }}
              yesLabel="OK"
            />
          </ModalFooter>
        </Modal>
      )}
      {!shared && active && (
        <Modal
          type="white"
          narrow
          active={active}
          toggleActive={() => {
            setActive(false)
          }}
        >
          <ModalHead>
            <Heading type={3} align="left" className={styles.heading}>
              Share anomaly
            </Heading>
          </ModalHead>
          <ModalBody>
            <div className={classNames({ [styles.loading]: loading })}>
              {loading && (
                <div className={styles.preloader}>
                  <Preloader style={{ height: 45, width: 60 }} />
                </div>
              )}
              <FormRow last>
                <LabelSlot noPadding column>
                  <Label id="generated-link" modalHeading>
                    Share link
                  </Label>
                </LabelSlot>
                <FieldSlot noPadding column>
                  <PassiveInput
                    id="generated-link"
                    value={typeof publicLink === 'undefined' ? '' : publicLink}
                    copy
                  />
                  <p className={styles.footNote}>
                    This link is currently {sharingLinkData.sharingPermission}.
                  </p>
                </FieldSlot>
              </FormRow>
              <FormRow last>
                <LabelSlot noPadding column>
                  <Label id="permission" modalHeading>
                    Who can see this
                  </Label>
                </LabelSlot>
                <FieldSlot noPadding column>
                  <SelectBoxSimple
                    key={permission}
                    name="permission"
                    value={permission}
                    className={styles.selectBox}
                    onChange={(permissionType) => {
                      setPermission(permissionType)
                    }}
                  >
                    {Object.keys(sharingPermissionTypes).map((key) => {
                      const item = sharingPermissionTypes[key]
                      return (
                        <option value={key} key={key}>
                          {item.long}
                        </option>
                      )
                    })}
                  </SelectBoxSimple>
                </FieldSlot>
              </FormRow>
              <FormRow last>
                <LabelSlot noPadding column>
                  <Label id="share-with" modalHeading>
                    Share with via email
                  </Label>
                  <ToolTip
                    className={styles.tooltip}
                    message="Add multiple users by separating email addresses with commas."
                  />
                </LabelSlot>
                <FieldSlot noPadding column>
                  <AddMultiValuesTags
                    type="modal"
                    placeholder="Add email or find teammates"
                    possibleValues={userList}
                    enableCommaMultiItems
                    onKeyUp={(val: string) => {
                      setTypedValue(val)
                    }}
                    onChange={(val: string[]) => {
                      setShareEmails(val)
                    }}
                  />
                </FieldSlot>
              </FormRow>
              <FormRow last>
                <LabelSlot noPadding column>
                  <Label id="note" modalHeading optional="(optional)">
                    Note
                  </Label>
                </LabelSlot>
                <FieldSlot noPadding column>
                  <Input
                    id="note"
                    name="note"
                    type="textArea"
                    value={note}
                    onChange={(val: string): void => {
                      setNote(val)
                    }}
                  />
                </FieldSlot>
              </FormRow>
            </div>
          </ModalBody>
          <ModalFooter>
            <ConfirmationButtons
              type="simple"
              onYes={(): void => {
                // eslint-disable-next-line
                ;(async function getContent() {
                  const useShareEmails = getShareEmails(
                    shareEmails,
                    typedValue,
                  ).filter((i) => i !== '' && i.indexOf('@') !== -1)
                  if (useShareEmails.length === 0) {
                    setLoading(false)
                    return
                  }
                  const data = {
                    pageData: {
                      anomDate: moment(anomalyDate, 'YYYY-MM-DD').format(
                        'Do MMM YYYY',
                      ),
                      metric: displayName,
                      siteSection: 'explain',
                    },
                    shareLinkID,
                    shareEmails: useShareEmails,
                    note,
                  }
                  if (shareLinkID === '' && publicLink !== '') {
                    const sl = publicLink.split('/').pop()
                    data.shareLinkID = sl
                  }
                  // const result = await shareSharingLink(data)
                  await shareSharingLink(data)
                  setSharingLinkData({
                    ...sharingLinkData,
                    sharingPermission: permission,
                    shared: true,
                  })
                  setLoading(false)
                })()
              }}
              onNo={(): void => {
                setActive(false)
              }}
              disabled={
                getShareEmails(shareEmails, typedValue).filter((i) => i !== '')
                  .length === 0
              }
              yesLabel={permission === 'off' ? 'Share' : 'Share'}
              noLabel="Go back"
            />
          </ModalFooter>
        </Modal>
      )}
    </div>
  )
}
